import type { ButtonHTMLAttributes, ReactNode } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { IconName } from './icon';
import { Icon } from './icon';

/*
 * Figma „Button“: primary = yellow with the square top-right corner, text = icon + label without
 * a background (inverse on dark surfaces). The arrow layer of the component is hidden everywhere.
 * Mobile frames use the 48 px / radius 12 size, desktop 50 px / radius 15.
 */
export type ButtonVariant = 'primary' | 'text' | 'text-inverse';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'gap-3 rounded-[12px] rounded-tr-none md:rounded-[15px] md:rounded-tr-none bg-[#f1c40f] text-[#14171a] shadow-[0_8px_32px_rgba(241,196,15,0.4)] hover:bg-[#f7d63e] active:bg-[#d9ae05]',
  text: 'gap-2 text-[#14171a] backdrop-blur-[4px] hover:text-[#2c7a25]',
  'text-inverse': 'gap-2 text-white backdrop-blur-[4px] hover:text-[#b2d6aa]',
};

export function buttonClassName(variant: ButtonVariant = 'primary', className?: string) {
  return cn(
    'svt-button inline-flex h-12 shrink-0 items-center justify-center whitespace-nowrap px-4 transition-colors md:h-[50px]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2',
    VARIANTS[variant],
    className,
  );
}

type ButtonContentProps = {
  variant?: ButtonVariant;
  icon?: IconName;
  children: ReactNode;
  className?: string;
};

function ButtonContent({ icon, children }: Pick<ButtonContentProps, 'icon' | 'children'>) {
  return (
    <>
      {icon ? <Icon name={icon} className="size-6" /> : null}
      <span>{children}</span>
    </>
  );
}

export function ButtonLink({
  href,
  variant,
  icon,
  newTab,
  children,
  className,
}: ButtonContentProps & { href: string; newTab?: boolean }) {
  return (
    <Link
      href={href}
      prefetch={false}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={buttonClassName(variant, className)}
    >
      <ButtonContent icon={icon}>{children}</ButtonContent>
    </Link>
  );
}

export function Button({
  variant,
  icon,
  children,
  className,
  type = 'button',
  ...props
}: ButtonContentProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'>) {
  return (
    <button type={type} className={buttonClassName(variant, className)} {...props}>
      <ButtonContent icon={icon}>{children}</ButtonContent>
    </button>
  );
}
